/**
 * Lou's realtime connection.
 *
 * The assistant runs its own Ably client, separate from the app's — different
 * credentials, a different backend issuing them, and channels nothing else in
 * the product subscribes to.
 *
 * Identity is the user and the conversation. There is no tab id: a channel
 * named after the browser tab could only ever be delivered to that one tab,
 * which is exactly why a conversation could not previously be picked up
 * anywhere else. Two pages open on the same conversation now attach to the
 * same channel and both see everything, which is the intended behaviour
 * rather than a case to defend against.
 *
 * Two channels, with different jobs:
 *
 * - the control channel is attached for as long as the user is signed in. It
 *   is quiet — a few messages when a conversation is created, renamed or
 *   finishes a turn — and it is what keeps the history list current on a
 *   device the user is not typing on.
 * - a conversation channel is attached only while that conversation is open.
 *   It carries the whole of a turn and is silent the rest of the time.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as Ably from "ably";
import { useDispatch, useSelector } from "react-redux";
import { AI_ASSISTANT_CONSTANTS } from "../constants/aiAssistantConstants";
import { LOU_EVENT_NAME } from "../louCommandBridge/louChannels";
import { isTraced, louLog, louWarn } from "../louCommandBridge/louLog";
import { fetchAblyToken } from "../utils/conversationApi";
import { handleLouCommand } from "../louCommandBridge/commandRouter";
import {
  loadConversations,
  openConversation,
  recallLastConversation,
  startNewConversation,
} from "../state/conversationActions";

const DEFAULT_CONTEXT = {
  ablyClient: null,
  userId: null,
  conversationId: null,
  isReady: false,
  realtimeConnectionState: "idle",
  isReachable: null,
  openConversationById: () => {},
  startConversation: () => {},
};

const AIAssistantAblyContext = createContext(DEFAULT_CONTEXT);

/** `?conversation=` makes a conversation a link, and survives a reload. */
const conversationFromUrl = () => {
  try {
    return new URLSearchParams(window.location.search).get("conversation");
  } catch {
    return null;
  }
};

const writeConversationToUrl = (conversationId) => {
  try {
    const url = new URL(window.location.href);
    if (conversationId) {
      url.searchParams.set("conversation", conversationId);
    } else {
      url.searchParams.delete("conversation");
    }
    window.history.replaceState({}, "", url.toString());
  } catch {
    // A URL we cannot rewrite costs a deep link, nothing more.
  }
};

export const AIAssistantAblyProvider = ({ children }) => {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state?.userDetail?.id ?? null);
  const conversationId = useSelector(
    (state) => state?.chat?.activeConversationId ?? null,
  );
  const [realtimeConnectionState, setRealtimeConnectionState] = useState("idle");
  // Whether the assistant's HTTP surface answers at all - distinct from the
  // realtime state. Realtime down only costs live streaming; HTTP down means
  // a message cannot be sent, and the composer has to say so instead of
  // accepting text that goes nowhere.
  //
  // `null` until the first attempt settles, so nothing is claimed on load.
  const [isReachable, setIsReachable] = useState(null);
  const [retryTick, setRetryTick] = useState(0);
  // Channel names come from the backend, never built here.
  //
  // They are keyed by whoever the backend decides the caller is, which is the
  // subject of the auth token - not `userDetail.id`. Those are different
  // identifiers, so a name assembled on this side would subscribe to a
  // channel nothing ever publishes to, and the chat would sit silent with
  // every request returning 200.
  const [channels, setChannels] = useState(null);
  const heartbeatRef = useRef(null);

  // -- the client ----------------------------------------------------------
  // Keyed on the user alone. A token covers `lou:v2:{user_id}:*`, so opening
  // a different conversation never needs a new one, and switching
  // conversations does not tear the connection down.
  const ablyClient = useMemo(() => {
    if (!userId) return null;
    return new Ably.Realtime({
      // No `clientId`: the token carries one and it is authoritative.
      // Asserting `userDetail.id` here makes Ably refuse the connection,
      // because it is a different identifier for the same person.
      closeOnUnload: false,
      autoConnect: true,
      // `authCallback`, not `authUrl`: the endpoint returns
      // `{ token_request, channels }`, and `authUrl` requires a bare token
      // request. This unwraps the envelope and keeps the channel names.
      authCallback: async (_tokenParams, callback) => {
        try {
          const data = await fetchAblyToken();
          louLog("channels resolved", data?.channels);
          setChannels(data?.channels ?? null);
          callback(null, data?.token_request);
        } catch (error) {
          louWarn("token request failed", error);
          callback(error, null);
        }
      },
    });
  }, [userId]);

  useEffect(() => {
    if (!ablyClient) {
      setRealtimeConnectionState("idle");
      return undefined;
    }
    const onState = (change) => {
      const next = change?.current || change?.currentState || change;
      if (typeof next === "string" && next.trim()) setRealtimeConnectionState(next);
    };
    const watched = [
      "initialized",
      "connecting",
      "connected",
      "disconnected",
      "suspended",
      "closing",
      "closed",
      "failed",
    ];
    watched.forEach((state) => ablyClient.connection.on(state, onState));
    watched.forEach((state) =>
      ablyClient.connection.on(state, () => louLog(`connection ${state}`)),
    );
    onState({ current: ablyClient.connection.state || "connecting" });

    heartbeatRef.current = setInterval(() => {
      // Only when connected: pinging a client that is still reconnecting
      // throws, and that error says nothing the connection state does not.
      if (ablyClient.connection.state !== "connected") return;
      ablyClient.connection
        .ping()
        .catch((error) => louWarn("heartbeat failed", error));
    }, AI_ASSISTANT_CONSTANTS.ABLY.HEARTBEAT_INTERVAL_MS);

    return () => {
      watched.forEach((state) => ablyClient.connection.off(state, onState));
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
      ablyClient.close();
    };
  }, [ablyClient]);

  // -- the control channel -------------------------------------------------
  // Attached regardless of what is on screen, so a turn finishing in a
  // conversation the user is not reading still updates their history.
  useEffect(() => {
    if (!ablyClient || !channels?.control) return undefined;
    const channel = ablyClient.channels.get(channels.control);
    const onMessage = (message) => {
      if (isTraced(message?.data?.command)) {
        louLog("control <-", {
          command: message?.data?.command,
          params: message?.data?.params,
        });
      }
      dispatch(handleLouCommand(message?.data));
    };
    louLog("subscribing control", channels.control);
    channel.subscribe(LOU_EVENT_NAME, onMessage);
    channel.on("attached", () => louLog("control attached", channel.name));
    channel.on("failed", (change) =>
      louWarn("control FAILED - the token probably does not cover this name", {
        channel: channel.name,
        reason: change?.reason,
      }),
    );
    return () => {
      channel.unsubscribe(LOU_EVENT_NAME, onMessage);
      channel.detach().catch(() => {});
    };
  }, [ablyClient, channels?.control, dispatch]);

  // -- the open conversation's channel -------------------------------------
  useEffect(() => {
    if (!ablyClient || !channels?.conversation_template || !conversationId) {
      return undefined;
    }
    const name = channels.conversation_template.replace(
      "{conversation_id}",
      conversationId,
    );
    const channel = ablyClient.channels.get(name);
    const onMessage = (message) => {
      const command = message?.data?.command;
      if (isTraced(command)) {
        const meta = message?.data?.metadata || {};
        louLog(`conv <- seq=${meta.seq} ${command}`, {
          turn_id: meta.turn_id,
          params: message?.data?.params,
        });
      }
      dispatch(handleLouCommand(message?.data));
    };
    louLog("subscribing conversation", { name, conversationId });
    channel.subscribe(LOU_EVENT_NAME, onMessage);
    channel.on("failed", (change) =>
      louWarn("conversation channel FAILED - token may not cover this name", {
        channel: name,
        reason: change?.reason,
      }),
    );
    channel.on("detached", () => louWarn("conversation channel detached", name));

    // Replay anything sent while the connection was down. `resumed` means
    // it was continuous and nothing was missed.
    const onAttached = (stateChange) => {
      louLog("conversation attached", {
        name,
        resumed: Boolean(stateChange?.resumed),
      });
      if (stateChange?.resumed) return;
      channel
        .history({ untilAttach: true, limit: 100 })
        .then((page) => {
          // Only what the user missed reading — never what they missed
          // seeing. A screen command from a turn that has already finished is
          // stale by the time it is replayed, and applying it would move the
          // dashboard somewhere nobody asked it to go. The transcript path
          // has always held this rule; the gap was that this one did not.
          const missed = [...(page.items || [])]
            .reverse()
            .filter((item) =>
              String(item?.data?.command ?? "").startsWith("conversation."),
            );
          louLog(`replaying ${missed.length} missed message(s)`, name);
          missed.forEach((item) => dispatch(handleLouCommand(item?.data)));
        })
        .catch((error) => louWarn("could not replay history", error));
    };
    channel.on("attached", onAttached);

    return () => {
      channel.off("attached", onAttached);
      channel.unsubscribe(LOU_EVENT_NAME, onMessage);
      channel.detach().catch(() => {});
    };
  }, [ablyClient, channels?.conversation_template, conversationId, dispatch]);

  // -- which conversation to show ------------------------------------------
  const openConversationById = useCallback(
    (id) => {
      writeConversationToUrl(id);
      return dispatch(openConversation(id));
    },
    [dispatch],
  );

  const startConversation = useCallback(async () => {
    const conversation = await dispatch(startNewConversation());
    writeConversationToUrl(conversation?.id);
    return conversation;
  }, [dispatch]);

  const bootstrapped = useRef(false);
  useEffect(() => {
    if (!userId || bootstrapped.current) return;
    bootstrapped.current = true;
    let cancelled = false;
    (async () => {
      try {
        const listed = await dispatch(loadConversations());
        if (cancelled) return;
        // `loadConversations` swallows its own failure and returns null, so
        // null here is the signal that the service did not answer.
        if (listed === null) throw new Error("conversations unavailable");

        // A link wins over what this browser last had open, so a shared URL
        // opens what it points at.
        const wanted =
          conversationFromUrl() ||
          recallLastConversation() ||
          listed?.conversations?.[0]?.id;
        if (wanted) {
          await openConversationById(wanted);
        } else {
          await startConversation();
        }
        if (!cancelled) setIsReachable(true);
      } catch (error) {
        if (cancelled) return;
        louWarn("assistant unreachable", error);
        setIsReachable(false);
        // Let the next tick try again. The outage is usually shorter than the
        // session, and a user who has to reload the dashboard to get Lou back
        // will assume it is gone for good.
        bootstrapped.current = false;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, dispatch, openConversationById, startConversation, retryTick]);

  // Retries only while it is known to be down, and stops once it is back.
  useEffect(() => {
    if (isReachable !== false) return undefined;
    const timer = setInterval(
      () => setRetryTick((tick) => tick + 1),
      AI_ASSISTANT_CONSTANTS.ABLY.REACHABILITY_RETRY_MS,
    );
    return () => clearInterval(timer);
  }, [isReachable]);

  const value = useMemo(
    () => ({
      ablyClient,
      userId,
      conversationId,
      isReady: Boolean(conversationId) && realtimeConnectionState === "connected",
      realtimeConnectionState,
      isReachable,
      openConversationById,
      startConversation,
    }),
    [
      ablyClient,
      userId,
      conversationId,
      realtimeConnectionState,
      isReachable,
      openConversationById,
      startConversation,
    ],
  );

  return (
    <AIAssistantAblyContext.Provider value={value}>
      {children}
    </AIAssistantAblyContext.Provider>
  );
};

export const useAssistantAbly = () =>
  useContext(AIAssistantAblyContext) || DEFAULT_CONTEXT;

export { AIAssistantAblyContext };
export default AIAssistantAblyContext;
